import type { CSSProperties, FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { AdminRole, AdminSession } from '@/types/marketplace'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'

import {
    Activity,
    ArrowUpRight,
    BookOpen,
    ChevronDown,
    ChevronsUpDown,
    ExternalLink,
    Flag,
    LayoutDashboard,
    LogOut,
    Package,
    Search,
    Settings,
    ShieldCheck,
    Sparkles,
    Stethoscope,
    Store,
    Tags,
    Target,
    UserCog,
    Users,
    Wand2
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { canAdministerPlatform } from '@/lib/auth/permissions'
import { NAV_ROW, NAV_SUB_ROW } from '@/components/admin/nav-row'
import { LogoMark } from '@/components/marketplace/logo'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { TooltipProvider } from '@/components/ui/tooltip'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarProvider,
    SidebarRail,
    SidebarSeparator,
    useSidebar
} from '@/components/ui/sidebar'

export type AdminBadges = {
    pending?: number
    reports?: number
    imported?: number
}

type NavChild = {
    href: string
    label: string
}

export type NavItem = {
    href: string
    label: string
    icon: LucideIcon | FC<{ className?: string }>
    badge?: 'pending' | 'reports'
    /** Real, filtered views of the same page — never decorative sub-links. */
    children?: Array<NavChild>
    /** Opens the public marketplace in a new tab. */
    external?: boolean
}

export type NavSection = {
    heading: string | null
    items: Array<NavItem>
    /**
     * The capability a session must hold for this group to be rendered.
     *
     * It is the SAME predicate the route behind the group runs server-side, read
     * from `@/lib/auth/permissions`, so the two cannot drift: a role that would
     * be bounced back to the dashboard never sees the entry in the first place.
     * Hiding it is UX only — the page loader and the API guard are the security
     * boundary and still refuse a direct URL.
     */
    capability?: (role: AdminRole) => boolean
}

/**
 * Sidebar structure. Headings are information architecture rather than
 * decoration: they group by what an operator is trying to do. Every child entry
 * is a query-filtered view the page's own server loader already validates, so a
 * submenu lands on live data rather than a placeholder route.
 */
const NAV: Array<NavSection> = [
    {
        heading: null,
        items: [
            { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
            /**
             * Escalations only. Routine listing review lives in each
             * marketplace's own console, so the badge counts what needs the
             * PLATFORM rather than every unreviewed listing everywhere.
             */
            { href: '/admin/moderation', label: 'Platform review', icon: ShieldCheck, badge: 'pending' }
        ]
    },
    {
        heading: 'Marketplace',
        items: [
            {
                href: '/admin/listings',
                label: 'Listings',
                icon: Package,
                children: [
                    { href: '/admin/listings?status=pending_review', label: 'Pending review' },
                    { href: '/admin/listings?status=active', label: 'Active' },
                    { href: '/admin/listings?status=sold', label: 'Sold' }
                ]
            },
            /**
             * The one-time enrichment backfill. It sits beside Listings because
             * it is the same stock seen from a different angle: what each
             * listing is filed under and what it shows without a photo.
             */
            { href: '/admin/enrichment', label: 'Listing preparation', icon: Wand2 },
            {
                href: '/admin/sellers',
                label: 'Sellers',
                icon: Users,
                children: [
                    { href: '/admin/sellers?verified=yes', label: 'Verified' },
                    { href: '/admin/sellers?status=suspended', label: 'Suspended' }
                ]
            },
            {
                href: '/admin/reports',
                label: 'Reports',
                icon: Flag,
                badge: 'reports',
                children: [
                    { href: '/admin/reports?status=open', label: 'Open' },
                    { href: '/admin/reports?status=resolved', label: 'Resolved' }
                ]
            }
        ]
    },
    {
        heading: 'Catalog',
        items: [
            { href: '/admin/categories', label: 'Categories', icon: Tags },
            {
                href: '/admin/promotions',
                label: 'Promotions',
                icon: Sparkles,
                children: [
                    // These values must match the states `/admin/promotions`
                    // actually accepts. They used to read `running` and `ended`,
                    // which the page rejected, so both links silently fell
                    // through to the unfiltered ledger.
                    { href: '/admin/promotions?state=active', label: 'Active' },
                    { href: '/admin/promotions?state=scheduled', label: 'Scheduled' },
                    { href: '/admin/promotions?state=expired', label: 'Expired' }
                ]
            }
        ]
    },
    {
        heading: 'Channels',
        items: [
            { href: '/admin/telegram', label: 'Telegram', icon: TelegramGlyph }
        ]
    },
    {
        heading: 'Platform',
        capability: canAdministerPlatform,
        items: [
            { href: '/admin/communities', label: 'Marketplaces', icon: Store },
            /*
             * The curated directory behind the /telegram-groups SEO pages. It
             * sits beside Marketplaces because it is where future marketplaces
             * are found: a listed group whose owner connects becomes one.
             */
            { href: '/admin/directory', label: 'Telegram directory', icon: BookOpen }
        ]
    },
    /**
     * The private acquisition CRM, deliberately its own section: the directory
     * above is PUBLIC discovery, this is who Bazio might invite and how that
     * went. Gated to the platform exactly as the directory is.
     */
    {
        heading: 'Growth',
        capability: canAdministerPlatform,
        items: [
            {
                href: '/admin/growth/telegram-leads',
                label: 'Telegram leads',
                icon: Target,
                children: [
                    { href: '/admin/growth/telegram-leads/follow-ups', label: 'Follow-ups' },
                    { href: '/admin/growth/telegram-leads/find', label: 'Find leads' },
                    { href: '/admin/growth/telegram-leads/templates', label: 'Templates' }
                ]
            }
        ]
    },
    /**
     * Operational tooling, gated to the platform exactly as Marketplaces is.
     *
     * The debugger acts on the environment bot that every marketplace's sign-in
     * depends on, so it belongs here rather than under Channels beside the
     * day-to-day Telegram settings page.
     */
    {
        heading: 'Tools',
        capability: canAdministerPlatform,
        items: [
            { href: '/admin/tools/telegram-debugger', label: 'Telegram debugger', icon: Stethoscope },
            /*
             * Different question from the debugger, which is why it is a
             * separate entry rather than a tab: that one asks "is the platform
             * bot healthy?", this one asks "did this message become a listing,
             * and where did it stop?".
             */
            { href: '/admin/tools/canary', label: 'Canary', icon: Activity }
        ]
    }
]

/**
 * The sections one session may see, in order.
 *
 * The sidebar renders exactly this, and nothing else decides visibility — so
 * the rule can be exercised without mounting the console, against the same
 * capability the route behind each group runs server-side. Hiding a group stays
 * UX only: `requireAdminApi({ platform: true })` and the page loaders re-check
 * on every request, and `platform-gate.test.ts` asserts the two never disagree.
 */
export const visibleNavSections = (role: AdminRole): Array<NavSection> =>
    NAV.filter((section) => !section.capability || section.capability(role))

/** The bottom utility group, above the account block. */
const UTILITY: Array<NavItem> = [
    { href: '/admin/settings', label: 'Settings', icon: Settings },
    { href: '/admin/users', label: 'Users and roles', icon: UserCog },
    { href: '/', label: 'View shop', icon: ArrowUpRight, external: true }
]

/* ------------------------------------------------------------------ *
 *  Shared row styling
 * ------------------------------------------------------------------ */

/**
 * Both consoles render the same row. The definition lives in
 * `@/components/admin/nav-row` so the Community Console cannot drift away from
 * it visually while staying a separate navigation.
 */
const ROW = NAV_ROW
const SUB_ROW = NAV_SUB_ROW

/**
 * Row counts. Waiting work is a quiet neutral chip; a reported listing is the
 * one thing in the panel that genuinely needs attention, so it alone carries
 * the brand red. The number is always paired with a text label for assistive
 * tech, so colour is never the only thing saying "this is urgent".
 */
const NavCount: FC<{ count: number, urgent?: boolean }> = ({ count, urgent = false }): ReactNode => (
    <span
        aria-hidden='true'
        className={cn(
            'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold leading-none tabular-nums',
            urgent
                ? 'bg-adm-nav-alert text-white'
                : 'bg-adm-nav-active text-adm-nav-ink'
        )}
    >
        {count > 99 ? '99+' : count}
    </span>
)

/* ------------------------------------------------------------------ *
 *  Navigation
 * ------------------------------------------------------------------ */

interface NavGroupProps {
    section: NavSection
    badges: AdminBadges
    onNavigate: () => void
}

const NavGroup: FC<NavGroupProps> = ({ section, badges, onNavigate }): ReactNode => {
    const router = useRouter()
    const { state, isMobile } = useSidebar()

    const rail = state === 'collapsed' && !isMobile

    // View state only, and identical on the server and the first client render.
    const [shut, setShut] = useState(false)
    const [shutItems, setShutItems] = useState<Record<string, boolean>>({})

    const path = router.pathname
    const current = router.asPath

    const isActive = (href: string): boolean => href === '/admin'
        ? path === '/admin'
        : path.startsWith(href)

    const open = rail || !shut

    return (
        <SidebarGroup className='gap-1 px-2 py-0'>
            {section.heading && (
                <SidebarGroupLabel
                    render={(
                        <button
                            type='button'
                            onClick={() => setShut((value) => !value)}
                            aria-expanded={!shut}
                        />
                    )}
                    className='h-7 gap-1 rounded-adm-nav px-2.5 text-[11px] font-medium uppercase tracking-[0.06em] text-adm-nav-muted transition-colors duration-150 hover:text-adm-nav-ink-strong motion-reduce:transition-none'
                >
                    <span className='truncate'>
                        {section.heading}
                    </span>

                    <ChevronDown
                        className={cn(
                            'size-3 shrink-0 transition-transform duration-150 motion-reduce:transition-none',
                            shut && '-rotate-90'
                        )}
                        aria-hidden='true'
                    />
                </SidebarGroupLabel>
            )}

            {open && (
                <SidebarGroupContent>
                    <SidebarMenu className='gap-px'>
                        {section.items.map((item) => {
                            const active = isActive(item.href)
                            const count = item.badge ? badges[item.badge] ?? 0 : 0

                            // The section you are working in opens itself; anywhere
                            // else the chevron is what opens it.
                            const openSub = shutItems[item.href] === undefined
                                ? active
                                : !shutItems[item.href]

                            return (
                                <SidebarMenuItem key={item.href}>
                                    <SidebarMenuButton
                                        isActive={active}
                                        tooltip={item.label}
                                        className={ROW}
                                        render={(
                                            <Link
                                                href={item.href}
                                                onClick={onNavigate}
                                                aria-current={active && current === item.href ? 'page' : undefined}
                                            />
                                        )}
                                    >
                                        <item.icon aria-hidden='true' />

                                        <span className='min-w-0 flex-1 truncate'>
                                            {item.label}
                                        </span>

                                        {count > 0 && (
                                            <span className='group-data-[collapsible=icon]:hidden'>
                                                <NavCount count={count} urgent={item.badge === 'reports'} />

                                                <span className='sr-only'>
                                                    {item.badge === 'reports'
                                                        ? `${count} open reports`
                                                        : `${count} waiting for review`}
                                                </span>
                                            </span>
                                        )}
                                    </SidebarMenuButton>

                                    {item.children && (
                                        <>
                                            <SidebarMenuAction
                                                onClick={() => setShutItems((value) => ({
                                                    ...value,
                                                    [item.href]: openSub
                                                }))}
                                                aria-expanded={openSub}
                                                aria-label={`${openSub ? 'Collapse' : 'Expand'} ${item.label} views`}
                                                className='top-2 rounded-adm-nav text-adm-nav-muted transition-colors duration-150 hover:bg-adm-nav-hover hover:text-adm-nav-ink-strong motion-reduce:transition-none'
                                            >
                                                <ChevronDown
                                                    className={cn(
                                                        'size-3.5 transition-transform duration-150 motion-reduce:transition-none',
                                                        !openSub && '-rotate-90'
                                                    )}
                                                    aria-hidden='true'
                                                />
                                            </SidebarMenuAction>

                                            {openSub && (
                                                <SidebarMenuSub className='mx-[15px] gap-px border-adm-nav-line px-2.5 py-0.5'>
                                                    {item.children.map((child) => (
                                                        <SidebarMenuSubItem key={child.href}>
                                                            <SidebarMenuSubButton
                                                                isActive={current === child.href}
                                                                className={SUB_ROW}
                                                                render={(
                                                                    <Link
                                                                        href={child.href}
                                                                        onClick={onNavigate}
                                                                        aria-current={current === child.href ? 'page' : undefined}
                                                                    />
                                                                )}
                                                            >
                                                                <span className='truncate'>
                                                                    {child.label}
                                                                </span>
                                                            </SidebarMenuSubButton>
                                                        </SidebarMenuSubItem>
                                                    ))}
                                                </SidebarMenuSub>
                                            )}
                                        </>
                                    )}
                                </SidebarMenuItem>
                            )
                        })}
                    </SidebarMenu>
                </SidebarGroupContent>
            )}
        </SidebarGroup>
    )
}

/* ------------------------------------------------------------------ *
 *  Account block
 * ------------------------------------------------------------------ */

interface AccountBlockProps {
    session: AdminSession
    onSignOut: () => void
    signingOut: boolean
    onNavigate: () => void
}

const AccountBlock: FC<AccountBlockProps> = ({
    session,
    onSignOut,
    signingOut,
    onNavigate
}): ReactNode => {
    const handle = session.telegramUsername ? `@${session.telegramUsername}` : session.email

    const roleLabel = session.role === 'owner'
        ? 'Owner'
        : session.role === 'admin'
            ? 'Admin'
            : 'Moderator'

    const initials = (session.name || handle)
        .replace(/[@]/g, '')
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('')

    const menuItem = 'min-h-9 gap-2.5 rounded-adm-nav px-2 py-1.5 text-[13px] text-adm-nav-muted focus:bg-adm-nav-hover focus:text-adm-nav-ink-strong focus:**:text-adm-nav-ink-strong'

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger
                        render={(
                            <SidebarMenuButton
                                size='lg'
                                aria-label={`Account: ${session.name || handle}`}
                                className='h-12 gap-2.5 rounded-adm-nav px-2 text-adm-nav-ink transition-colors duration-150 hover:bg-adm-nav-hover focus-visible:ring-2 focus-visible:ring-adm-accent focus-visible:ring-offset-2 motion-reduce:transition-none data-open:bg-adm-nav-active'
                            />
                        )}
                    >
                        <Avatar size='sm' className='size-8 shrink-0 rounded-full after:rounded-full'>
                            {session.photoUrl && (
                                <AvatarImage
                                    src={session.photoUrl}
                                    alt=''
                                    className='rounded-full'
                                />
                            )}

                            <AvatarFallback className='rounded-full bg-adm-nav-active text-[11px] font-semibold text-adm-nav-ink'>
                                {initials || 'MT'}
                            </AvatarFallback>
                        </Avatar>

                        <span className='min-w-0 flex-1 text-left'>
                            <span className='block truncate text-[13px] font-medium text-adm-nav-ink'>
                                {session.name || handle}
                            </span>

                            <span className='block truncate text-[11.5px] text-adm-nav-muted'>
                                {handle}
                            </span>
                        </span>

                        <ChevronsUpDown className='size-4 shrink-0 text-adm-nav-muted' aria-hidden='true' />
                    </DropdownMenuTrigger>

                    <DropdownMenuContent
                        align='start'
                        side='top'
                        sideOffset={8}
                        className='w-60 rounded-adm-nav border-adm-nav-line bg-adm-nav-surface p-1 shadow-adm-pop'
                    >
                        <div className='px-2 py-1.5'>
                            <p className='truncate text-[13px] font-medium text-adm-nav-ink'>
                                {session.name || handle}
                            </p>

                            <p className='mt-0.5 truncate text-[11.5px] text-adm-nav-muted'>
                                {roleLabel} · {handle}
                            </p>
                        </div>

                        <DropdownMenuSeparator className='bg-adm-nav-line' />

                        <DropdownMenuItem
                            render={<Link href='/admin/settings' onClick={onNavigate} />}
                            className={menuItem}
                        >
                            <Settings className='size-4 shrink-0' aria-hidden='true' />
                            Console settings
                        </DropdownMenuItem>

                        <DropdownMenuItem
                            render={<Link href='/admin/users' onClick={onNavigate} />}
                            className={menuItem}
                        >
                            <UserCog className='size-4 shrink-0' aria-hidden='true' />
                            Users and roles
                        </DropdownMenuItem>

                        <DropdownMenuItem
                            render={<Link href='/' target='_blank' rel='noopener noreferrer' />}
                            className={menuItem}
                        >
                            <ExternalLink className='size-4 shrink-0' aria-hidden='true' />
                            View marketplace
                        </DropdownMenuItem>

                        <DropdownMenuSeparator className='bg-adm-nav-line' />

                        <DropdownMenuItem
                            onClick={onSignOut}
                            disabled={signingOut}
                            className={menuItem}
                        >
                            <LogOut className='size-4 shrink-0' aria-hidden='true' />
                            {signingOut ? 'Signing out…' : 'Sign out'}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    )
}

/* ------------------------------------------------------------------ *
 *  Sidebar
 * ------------------------------------------------------------------ */

interface AdminSidebarProps {
    session: AdminSession
    badges: AdminBadges
    onSignOut: () => void
    signingOut: boolean
}

const AdminSidebar: FC<AdminSidebarProps> = ({
    session,
    badges,
    onSignOut,
    signingOut
}): ReactNode => {
    const router = useRouter()
    const { state, isMobile, setOpen, setOpenMobile } = useSidebar()
    const searchRef = useRef<HTMLInputElement | null>(null)

    const rail = state === 'collapsed' && !isMobile

    // Expanding from the rail to search should land the caret in the field.
    const [wantFocus, setWantFocus] = useState(false)

    useEffect(() => {
        if (!rail && wantFocus) {
            searchRef.current?.focus()
            setWantFocus(false)
        }
    }, [rail, wantFocus])

    /** Selecting anything in the mobile drawer closes it. */
    const onNavigate = (): void => {
        if (isMobile) setOpenMobile(false)
    }

    const submit = (): void => {
        const term = searchRef.current?.value.trim() ?? ''

        if (!term) return

        onNavigate()
        void router.push(`/admin/listings?q=${encodeURIComponent(term)}`)
    }

    return (
        <Sidebar collapsible='icon' className='border-adm-nav-line bg-adm-nav-surface'>
            <SidebarHeader className='gap-2 bg-adm-nav-surface p-0'>
                {/* ---------------- Brand ---------------- */}
                <div className={cn(
                    'flex h-14 shrink-0 items-center gap-1 px-2.5',
                    rail && 'justify-center px-0'
                )}>
                    <Link
                        href='/admin'
                        onClick={onNavigate}
                        aria-label='Bazio admin'
                        className={cn(
                            'adm-focus flex min-w-0 items-center gap-2.5 rounded-adm-nav px-1.5 py-1.5 transition-colors duration-150 hover:bg-adm-nav-hover motion-reduce:transition-none',
                            rail ? 'justify-center' : 'flex-1'
                        )}
                    >
                        {/*
                            The square `b` mark is black artwork on
                            transparency, and the console's navigation panel is
                            `--adm-nav-surface`, which is pure white — so it
                            reads at full contrast here with no plate. Never
                            move this mark onto a dark surface; the app icon is
                            the only supplied asset that survives one.
                        */}
                        <LogoMark className='h-[22px] w-auto shrink-0' />

                        {!rail && (
                            <span className='min-w-0 flex-1 truncate text-[14px] font-semibold tracking-[-0.015em] text-adm-nav-ink'>
                                Bazio
                            </span>
                        )}
                    </Link>
                </div>

                {/* ---------------- Search ---------------- */}
                {rail
                    ? (
                        <div className='flex justify-center pb-1'>
                            <button
                                type='button'
                                onClick={() => {
                                    setWantFocus(true)
                                    setOpen(true)
                                }}
                                aria-label='Search listings'
                                className='adm-focus inline-flex size-9 items-center justify-center rounded-adm-nav border border-adm-nav-field-line bg-adm-nav-surface text-adm-nav-ink transition-colors duration-150 hover:bg-adm-nav-hover motion-reduce:transition-none'
                            >
                                <Search className='size-4' aria-hidden='true' />
                            </button>
                        </div>
                    )
                    : (
                        <div className='px-3 pb-1'>
                            <label htmlFor='admin-search' className='sr-only'>
                                Search listings
                            </label>

                            <div className='relative'>
                                <Search
                                    className='pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-adm-nav-ink'
                                    aria-hidden='true'
                                />

                                <input
                                    id='admin-search'
                                    ref={searchRef}
                                    type='search'
                                    placeholder='Search listings'
                                    className='h-9 w-full rounded-adm-nav border border-adm-nav-field-line bg-adm-nav-surface pl-9 pr-2.5 text-[13px] text-adm-nav-ink outline-none transition-colors duration-150 placeholder:text-adm-nav-muted hover:bg-adm-nav-hover focus:border-adm-nav-ink focus:bg-adm-nav-surface focus:ring-2 focus:ring-adm-nav-active motion-reduce:transition-none [&::-webkit-search-cancel-button]:appearance-none'
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') submit()
                                        if (event.key === 'Escape') searchRef.current?.blur()
                                    }}
                                />
                            </div>
                        </div>
                    )}

                <SidebarSeparator className='mx-3 mt-1 bg-adm-nav-line' />
            </SidebarHeader>

            {/* ---------------- Sections ---------------- */}
            <SidebarContent className='gap-5 bg-adm-nav-surface py-3'>
                {visibleNavSections(session.role).map((section, index) => (
                    <NavGroup
                        key={section.heading ?? `section-${index}`}
                        section={section}
                        badges={badges}
                        onNavigate={onNavigate}
                    />
                ))}
            </SidebarContent>

            {/* ---------------- Utility + account ---------------- */}
            <SidebarFooter className='gap-2 border-t border-adm-nav-line bg-adm-nav-surface p-2'>
                <SidebarMenu className='gap-px'>
                    {UTILITY.map((item) => (
                        <SidebarMenuItem key={item.href + item.label}>
                            <SidebarMenuButton
                                isActive={!item.external && router.pathname.startsWith(item.href)}
                                tooltip={item.label}
                                className={ROW}
                                render={(
                                    <Link
                                        href={item.href}
                                        target={item.external ? '_blank' : undefined}
                                        rel={item.external ? 'noopener noreferrer' : undefined}
                                        onClick={onNavigate}
                                    />
                                )}
                            >
                                <item.icon aria-hidden='true' />

                                <span className='min-w-0 flex-1 truncate'>
                                    {item.label}
                                </span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    ))}
                </SidebarMenu>

                <AccountBlock
                    session={session}
                    onSignOut={onSignOut}
                    signingOut={signingOut}
                    onNavigate={onNavigate}
                />
            </SidebarFooter>

            <SidebarRail />
        </Sidebar>
    )
}

/* ------------------------------------------------------------------ *
 *  Layout
 * ------------------------------------------------------------------ */

const COOKIE = 'sidebar_state'

interface AdminLayoutProps {
    session: AdminSession
    badges?: AdminBadges
    children: ReactNode
}

/**
 * The persistent console shell. It is mounted ABOVE the page component (in
 * `_app`), so navigating between admin pages swaps only the content column:
 * the sidebar keeps its collapse state, its open sections and its scroll
 * position, and never remounts.
 *
 * The whole shell is exactly one viewport tall and clips its own overflow, so
 * the only scrolling surface in the console is the content column inside
 * `AdminShell`. Scrolling a long listings table cannot move the navigation.
 */
export const AdminLayout: FC<AdminLayoutProps> = ({ session, badges = {}, children }): ReactNode => {
    const router = useRouter()
    const [signingOut, setSigningOut] = useState(false)

    // Server-safe default, then the stored preference once mounted. The
    // primitive writes this cookie itself whenever the state changes.
    const [open, setOpen] = useState(true)

    useEffect(() => {
        const saved = document.cookie
            .split('; ')
            .find((entry) => entry.startsWith(`${COOKIE}=`))

        if (saved?.endsWith('false')) setOpen(false)
    }, [])

    const signOut = async (): Promise<void> => {
        setSigningOut(true)

        await fetch('/api/admin/auth/logout', { method: 'POST' }).catch(() => undefined)
        await router.replace('/admin')
    }

    return (
        <TooltipProvider delay={120}>
            <SidebarProvider
                open={open}
                onOpenChange={setOpen}
                style={{ '--sidebar-width': '15rem', '--sidebar-width-icon': '3.25rem' } as CSSProperties}
                className='adm-scope h-svh min-h-svh overflow-hidden bg-adm-bg font-sans text-adm-ink'
            >
                <AdminSidebar
                    session={session}
                    badges={badges}
                    onSignOut={() => void signOut()}
                    signingOut={signingOut}
                />

                <SidebarInset className='flex h-svh min-w-0 flex-col overflow-hidden bg-adm-bg'>
                    {children}
                </SidebarInset>
            </SidebarProvider>
        </TooltipProvider>
    )
}
